import {useState} from 'react';
import {Link} from 'react-router-dom';
import {api, jsonOptions} from '../api/client.js';
import {useStorefront} from '../hooks/useStorefront.js';
import {ProductRail} from '../components/commerce/ProductRail.jsx';
import {CategoryRail} from '../components/commerce/CategoryRail.jsx';
import {SectionHeader} from '../components/ui/SectionHeader.jsx';
import {LoadingState} from '../components/ui/AsyncState.jsx';
import {Icon} from '../components/Icon.jsx';

const referenceBanners = ['/design-assets/image-1.png', '/design-assets/image-4.png', '/design-assets/image-2.png', '/design-assets/image-3.png'];

function Hero({categories, slider}) {
  return <section className="hero-slider shell">
    <aside className="categorie-menu-fixed">
      <nav className="categorie-menu sg-active">
        {categories.slice(0, 9).map(category => <Link key={category.id} to={`/category/${category.slug}`}>{category.name}<span className="icon">›</span></Link>)}
        <Link className="last-menu-item" to="/categories">View All Categories<span className="icon">›</span></Link>
      </nav>
    </aside>
    <div className="hero-main">
      {slider?.image ? <Link className="hero-panel supplied-hero" to={slider.button_link || '/catalog'} aria-label={slider.button_text || 'Shop new arrivals'}><img src={slider.image} alt={slider.heading || 'New arrivals collection'}/></Link> : <div className="discovery-hero"><div><h1>Find your next favourite.</h1><p>Thoughtful picks for your everyday.</p><Link className="btn" to="/catalog">Shop collection <Icon name="chevron"/></Link></div><img src="/design-assets/auth-still-life.png" alt="Everyday essentials, from a sage shopping bag to cream sneakers" width="1536" height="1024"/></div>}
      <div className="hero-banner-section"><div className="banner__items">
        {referenceBanners.map((banner, index) => <Link className="banner__item" key={banner} to="/campaigns"><img src={banner} alt={`Promotional banner ${index + 1}`}/></Link>)}
      </div></div>
    </div>
  </section>;
}

function Services({items = []}) {
  if (!items.length) return null;
  return <section className="sg-convenience shell"><ul>
    {items.map(item => <li key={item.id}><div className="convenience"><div className="text"><b>{item.title}</b><span>{item.subtitle}</span></div></div></li>)}
  </ul></section>;
}

function Campaigns({campaigns = []}) {
  const cards = campaigns.slice(0, 3);
  if (!cards.length) return null;
  return <section className="section shell">
    <SectionHeader title="Campaign" to="/campaigns" action="More Campaign"/>
    <div className="campaign-grid">
      {cards.map(campaign => <Link className="campaign" key={campaign.id} to={campaign.slug ? `/campaign/${campaign.slug}` : '/campaigns'}>
        <img src={campaign.banner || '/design-assets/image.png'} alt={campaign.title || campaign.name}/>
        <div className="campaign-copy"><h2>{campaign.title || campaign.name}</h2><span className="campaign-action">Explore campaign <Icon name="chevron"/></span></div>
      </Link>)}
    </div>
  </section>;
}

function TopCategories({categories}) {
  return <div className="bg-color-band"><section className="shell">
    <SectionHeader title="Top Categories of This Month" to={null}/>
    <div className="category-circles">
      {categories.slice(0, 6).map(category => <Link key={category.id} to={`/catalog?category=${category.slug}`}><span><img src={category.image} alt="" loading="lazy"/></span><b>{category.name}</b></Link>)}
    </div>
  </section></div>;
}

function TopShops({shops = []}) {
  if (!shops.length) return null;
  return <section className="section shell">
    <SectionHeader title="Top Shop" to={null}/>
    <div className="shop-strip">
      {shops.map(shop => <article key={shop.id}>
        <span className="shop-cover"/>
        <div>{shop.logo ? <img src={shop.logo} alt=""/> : shop.name.slice(0, 2).toUpperCase()}</div>
        <b>{shop.name}{shop.verified ? ' ✓' : ''}</b>
        <small>Rating: {shop.rating || 0} ★</small>
        <Link to={`/shop/${shop.slug}`}>Visit Store <span aria-hidden="true">→</span></Link>
      </article>)}
    </div>
  </section>;
}

function Brands({brands = []}) {
  if (!brands.length) return null;
  return <section className="section shell">
    <SectionHeader title="Popular Brand" to="/brands" action="Browse All Brands"/>
    <div className="brand-strip">
      {brands.map(brand => <Link to={`/brand/${brand.slug}`} key={brand.id}>
        {brand.logo ? <img src={brand.logo} alt={brand.name}/> : <strong>{brand.name}</strong>}
        <span>{brand.name}</span>
      </Link>)}
    </div>
  </section>;
}

function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({state: 'idle', message: ''});
  async function submit(event) {
    event.preventDefault();
    if (status.state === 'busy') return;
    setStatus({state: 'busy', message: ''});
    try {
      const response = await api('/newsletter/subscribe', jsonOptions('POST', {email}));
      setStatus({state: 'done', message: response.data?.message || 'You are subscribed to the newsletter.'});
      setEmail('');
    } catch (requestError) {
      setStatus({state: 'error', message: requestError.details?.email?.[0] || requestError.message || 'Subscription failed. Please try again.'});
    }
  }
  return <section className="newsletter"><div>
    <div className="subscribe-text"><b>✉</b><span><strong>Subscribe to our news letter</strong><small>Get all the latest information on Events sales and Offers.</small></span></div>
    <div>
      {status.state === 'done'
        ? <p className="auth-feedback" role="status">✓ {status.message}</p>
        : <><form onSubmit={submit}>
            <input type="email" value={email} onChange={event => setEmail(event.target.value)} aria-label="Email address" placeholder="Email" required/>
            <button disabled={status.state === 'busy'}>{status.state === 'busy' ? 'Subscribing…' : 'Subscribe'}</button>
          </form>{status.state === 'error' ? <p className="auth-feedback error" role="alert">{status.message}</p> : null}</>}
    </div>
  </div></section>;
}

export function HomePage() {
  const {loading, data, error} = useStorefront();
  if (loading) return <div className="shell"><LoadingState label="Loading store…" rows={6}/></div>;
  if (error) return <div className="status">We could not load the store. Please refresh the page and try again.</div>;
  const [firstRail, ...otherRails] = data.sections;
  return <>
    <Hero categories={data.categories} slider={data.sliders?.[0]}/>
    <Services items={data.services}/>
    <Campaigns campaigns={data.campaigns}/>
    <CategoryRail categories={data.categories}/>
    {firstRail ? <ProductRail title={firstRail.title} products={firstRail.items} action="More Products"/> : null}
    <TopCategories categories={data.categories}/>
    {otherRails.map(section => <ProductRail key={section.key} title={section.title} products={section.items} action="More Products"/>)}
    <TopShops shops={data.shops}/>
    <Brands brands={data.brands}/>
    <Newsletter/>
  </>;
}
